import moment from 'moment';
import Entity from 'src/app/core/domain/entity';
import UniqueEntityID from 'src/app/core/domain/unique_entity_id';
import { UserId } from 'src/app/data/auth/domain/user';

import { MsdAiResult } from '../../../../core/domain/msd-ai-result';
import { PatientId } from '../../../patient/domain/patient';
import { DiaryType } from '../diary_type/diary_type';
import { DiaryTracker } from './diary_tracker';
import { Picture } from './picture';

export interface DiaryProps {
  patientId: PatientId;
  patientUserId?: UserId;
  createdAt?: Date;
  updatedAt?: Date;
  name?: string;
  type: string | undefined;
  diaryType?: DiaryType | undefined;
  pictures: Picture[];
  trackers: DiaryTracker[];
  archivedAt?: Date;
  comment?: string;
  isSharedComment: boolean;
  timeOffset?: number | undefined;
  msdAiResults?: MsdAiResult[] | undefined;
}

export class DiaryId extends Entity<unknown> {
  private constructor(id?: UniqueEntityID) {
    super(null, id);
  }

  public static create(id?: UniqueEntityID): DiaryId {
    return new DiaryId(id);
  }
}

export class Diary extends Entity<DiaryProps> {
  get diaryId(): DiaryId {
    return DiaryId.create(this.id);
  }

  get patientId(): PatientId {
    return this.props.patientId;
  }

  get patientUserId(): UserId | undefined {
    return this.props.patientUserId;
  }

  get createdAt(): Date | undefined {
    return this.props.createdAt;
  }

  set createdAt(value: Date | undefined) {
    this.props.createdAt = value;
  }

  // Date de saisie dans le fuseau du patient à ce moment-là
  get localCreatedAt(): moment.Moment | undefined {
    if (!this.createdAt) return undefined;
    return this.timeOffset !== undefined
      ? moment(this.createdAt).utcOffset(this.timeOffset)
      : moment(this.createdAt).local();
  }

  get updatedAt(): Date | undefined {
    return this.props.updatedAt;
  }

  get archivedAt(): Date | undefined {
    return this.props.archivedAt;
  }

  get timeOffset(): number | undefined {
    return this.props.timeOffset;
  }

  set timeOffset(value: number | undefined) {
    this.props.timeOffset = value;
  }

  get name(): string | undefined {
    return this.props.name;
  }

  set name(value: string | undefined) {
    this.props.name = value;
  }

  get type(): string | undefined {
    return this.props.type;
  }

  set type(value: string | undefined) {
    this.props.type = value;
  }

  get diaryType(): DiaryType | undefined {
    return this.props.diaryType;
  }

  set diaryType(value: DiaryType | undefined) {
    this.props.diaryType = value;
  }

  get pictures(): Picture[] {
    return this.props.pictures;
  }

  set pictures(pictures: Picture[] | undefined) {
    this.props.pictures = pictures ?? [];
  }

  get trackers(): DiaryTracker[] {
    return this.props.trackers;
  }

  set trackers(trackers: DiaryTracker[]) {
    this.props.trackers = trackers;
  }

  get givenTrackers(): DiaryTracker[] {
    return this.props.trackers.filter(
      (tracker) => !(tracker.value < (tracker.min ?? 0)),
    );
  }

  get comment(): string | undefined {
    return this.props.comment;
  }

  get isSharedComment(): boolean {
    return this.props.isSharedComment;
  }

  get msdAiResults(): MsdAiResult[] | undefined {
    return this.props.msdAiResults;
  }

  set msdAiResults(value: MsdAiResult[] | undefined) {
    this.props.msdAiResults = value;
  }

  private constructor(props: DiaryProps, id?: UniqueEntityID) {
    super(props, id);
  }

  copyWith(props: DiaryProps): Diary {
    const diaryCopy = Diary.create(
      {
        ...this.props,
        ...props,
      },
      this.id,
    );
    diaryCopy.pictures = diaryCopy.pictures.map((p) => p.copyWith(p.props));
    return diaryCopy;
  }

  public static create(props: DiaryProps, id?: UniqueEntityID): Diary {
    return new Diary(props, id);
  }
}
